import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  FiHome,
  FiShoppingCart,
  FiCoffee,
  FiUsers,
  FiFileText,
  FiSettings,
  FiHelpCircle,
  FiBarChart2,
  FiUserCheck,
  FiTable,
  FiBook,
  FiGrid,
  FiPackage,
  FiAward,
  FiBox,
  FiEdit,
  FiDollarSign,
  FiShoppingBag,
  FiTrendingUp,
  FiPieChart,
  FiMenu,
  FiX,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import { FaUtensils } from 'react-icons/fa';
import logo from '../../../../assets/elegantelogo.png';

// Modern Material-UI styled Sidebar with glossy red border
const Sidebar = ({ activeMenu, setActiveMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    menu: false,
    inventory: false,
    account: false,
  });

  const currentPath = location.pathname;

  // Auto-expand submenus based on current path
  useEffect(() => {
    const path = currentPath.toLowerCase();
    const newExpanded = { ...expandedMenus };

    if (path.includes('/menu') || path.includes('/categories') || path.includes('/items')) {
      newExpanded.menu = true;
    }
    if (path.includes('/inventory') || path.includes('/measurement') || path.includes('/stocks') || path.includes('/recipe')) {
      newExpanded.inventory = true;
    }
    if (path.includes('/account') || path.includes('/purchase')) {
      newExpanded.account = true;
    }

    setExpandedMenus(newExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const handleNavigation = (menuName, path) => {
    if (setActiveMenu) {
      setActiveMenu(menuName);
    }
    if (path && path !== '#') {
      navigate(path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path, exact = false) => {
    if (!path || path === '#') return false;
    if (exact) {
      return currentPath === path;
    }
    return currentPath.startsWith(path) || currentPath === path;
  };

  const isSubmenuActive = (subItems) => {
    if (!subItems) return false;
    return subItems.some((item) => isActive(item.path));
  };

  const menuItems = [
    {
      name: 'Dashboard',
      icon: <FiBarChart2 />,
      path: '/ManagerDashboard_L_consultz',
      exact: true,
    },
    {
      name: 'Tables',
      icon: <FiTable />,
      path: '/Lounge/Tables/consultz',
      exact: true,
    },
     {
      name: 'Menu',
      icon: <FiBook />,
      path: '/Lounge/MenuItems/consultants',
      exact: true,
    },
   
  ];

  // Shared glossy red border + white background styles for drawers
  const drawerPaperStyles = {
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    border: '1.5px solid #C62828',
    borderRadius: '0 20px 20px 0',
    boxShadow:
      '0 0 0 1px rgba(198, 40, 40, 0.12), 0 12px 28px -8px rgba(0, 0, 0, 0.12), 0 0 20px rgba(198, 40, 40, 0.18), inset 0 1px 3px rgba(255, 255, 255, 0.9)',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      border: '1px solid rgba(255, 255, 255, 0.7)',
      boxShadow: 'inset 0 1px 5px rgba(255, 255, 255, 0.8)',
      zIndex: 2,
    },
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 3,
      }}
    >
      {/* Brand Header — using your original logo */}
      <Box
        sx={{
          py: 2.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          minHeight: 72,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Elegante Restaurant Logo"
          sx={{
            height: 44,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Menu List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 1,
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d1d5db',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#b0b8c4',
          },
        }}
      >
        {/* Section Label */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.5,
            py: 1,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            fontSize: '0.65rem',
          }}
        >
          Main
        </Typography>

        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            const isItemActive = isActive(item.path, item.exact);
            const isSubActive = item.hasSubmenu && isSubmenuActive(item.subItems);
            const isExpanded = expandedMenus[item.name.toLowerCase()] || isSubActive;

            if (item.hasSubmenu) {
              return (
                <Box key={item.name}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => {
                        toggleMenu(item.name.toLowerCase());
                        if (item.path && item.path !== '#') {
                          navigate(item.path);
                        }
                      }}
                      selected={isItemActive || isSubActive}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.25,
                        minHeight: 44,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(198, 40, 40, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(198, 40, 40, 0.12)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: '#C62828',
                          },
                          '& .MuiListItemText-primary': {
                            color: '#C62828',
                            fontWeight: 700,
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isItemActive || isSubActive ? '#C62828' : 'text.secondary',
                          fontSize: 20,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isItemActive || isSubActive ? 700 : 600,
                          color: isItemActive || isSubActive ? '#C62828' : 'text.primary',
                          noWrap: true,
                        }}
                      />
                      {isExpanded ? (
                        <FiChevronDown size={16} style={{ color: '#b0b8c4' }} />
                      ) : (
                        <FiChevronRight size={16} style={{ color: '#b0b8c4' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      {item.subItems.map((subItem) => {
                        const isSubItemActive = isActive(subItem.path, true);
                        return (
                          <ListItem key={subItem.name} disablePadding>
                            <ListItemButton
                              onClick={() => handleNavigation(subItem.name, subItem.path)}
                              selected={isSubItemActive}
                              sx={{
                                borderRadius: 1.5,
                                pl: 5,
                                minHeight: 36,
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(198, 40, 40, 0.06)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(198, 40, 40, 0.10)',
                                  },
                                  '& .MuiListItemIcon-root': {
                                    color: '#C62828',
                                  },
                                  '& .MuiListItemText-primary': {
                                    color: '#C62828',
                                    fontWeight: 600,
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 32,
                                  color: isSubItemActive ? '#C62828' : 'text.secondary',
                                  fontSize: 16,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.name}
                                primaryTypographyProps={{
                                  fontSize: 13,
                                  fontWeight: isSubItemActive ? 600 : 500,
                                  color: isSubItemActive ? '#C62828' : 'text.secondary',
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem key={item.name} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.name, item.path)}
                  selected={isItemActive}
                  disabled={item.path === '#'}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.25,
                    minHeight: 44,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(198, 40, 40, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(198, 40, 40, 0.12)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: '#C62828',
                      },
                      '& .MuiListItemText-primary': {
                        color: '#C62828',
                        fontWeight: 700,
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isItemActive ? '#C62828' : 'text.secondary',
                      fontSize: 20,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isItemActive ? 700 : 600,
                      color: isItemActive ? '#C62828' : 'text.primary',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        {/* Support Section */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.5,
            py: 0.5,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            fontSize: '0.65rem',
          }}
        >
          Support(Contact ICT)
        </Typography>

        <List sx={{ py: 0 }}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => navigate('#')}
              sx={{
                borderRadius: 1.5,
                mb: 0.25,
                minHeight: 44,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: 'text.secondary',
                  fontSize: 20,
                }}
              >
                <FiHelpCircle />
              </ListItemIcon>
              <ListItemText
                primary="Help"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  noWrap: true,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Hamburger Button */}
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          bgcolor: 'background.paper',
          border: '1.5px solid #C62828',
          boxShadow: '0 4px 14px rgba(198, 40, 40, 0.25)',
          borderRadius: 2,
          '&:hover': {
            bgcolor: '#fff5f5',
          },
        }}
      >
        <FiMenu size={22} color="#C62828" />
      </IconButton>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            ...drawerPaperStyles,
            width: 280,
            m: 1.5,
            borderRadius: '20px',
            height: 'calc(100% - 24px)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            ...drawerPaperStyles,
            width: 260,
            position: 'sticky',
            top: 0,
            height: '100vh',
            borderRadius: '0 20px 20px 0',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;